/**
 * GEN-2566 region partitioning for Eaton DSP extract downloads.
 *
 * Canonical IM tabs: AB / BC / Midwest / ON / QC (always present, empty = headers only).
 * Unmapped labels (National, BRANCH, DOP, …) go to Other (omitted when empty).
 * Compound labels (e.g. Alberta_Midwest) expand onto every matching canonical tab.
 *
 * Mapping (case/spacing insensitive; split compounds on comma/underscore):
 *   AB      ← AB, Alberta, Alberta…
 *   BC      ← BC, Pacific (Westburne Pacific ≈ BC coastal)
 *   Midwest ← Midwest
 *   ON      ← ON, Ont, Ontario, Central
 *   QC      ← QC, Que, Quebec
 *   Other   ← National, National but ATL, BRANCH, DOP, unmatched
 */

export const CANONICAL_REGIONS = ['AB', 'BC', 'Midwest', 'ON', 'QC'] as const;

export type CanonicalRegion = (typeof CANONICAL_REGIONS)[number];
export type PartitionRegion = CanonicalRegion | 'Other';

const EXACT_TOKEN_MAP: Record<string, PartitionRegion> = {
  ab: 'AB',
  alberta: 'AB',
  bc: 'BC',
  pacific: 'BC',
  midwest: 'Midwest',
  on: 'ON',
  ont: 'ON',
  ontario: 'ON',
  central: 'ON',
  qc: 'QC',
  que: 'QC',
  quebec: 'QC',
  national: 'Other',
  'national but atl': 'Other',
  branch: 'Other',
  dop: 'Other',
};

function normalizeToken(raw: string): string {
  return raw.trim().toLowerCase().replace(/\s+/g, ' ');
}

function mapToken(token: string): PartitionRegion | null {
  const normalized = normalizeToken(token);
  if (!normalized) return null;
  const exact = EXACT_TOKEN_MAP[normalized];
  if (exact) return exact;
  // Alberta_Midwest-style leftovers after split still start with alberta
  if (normalized.startsWith('alberta')) return 'AB';
  return null;
}

/** Split a raw region label into partition destinations (deduped, stable order). */
export function partitionRegionsForLabel(
  regionLabel: string,
): PartitionRegion[] {
  const raw = regionLabel?.trim() ?? '';
  if (!raw) return ['Other'];

  // Prefer exact full-string match (e.g. "National but ATL") before splitting.
  const full = mapToken(raw);
  const hasCompoundSeparator = /[,_]/.test(raw);
  if (full && !hasCompoundSeparator) {
    return [full];
  }

  const tokens = raw
    .split(/[,_]+/)
    .map((part) => part.trim())
    .filter(Boolean);
  const parts = tokens.length > 0 ? tokens : [raw];

  const canonical = new Set<CanonicalRegion>();
  let sawOther = false;
  let sawUnmapped = false;

  for (const part of parts) {
    const mapped = mapToken(part);
    if (!mapped) {
      sawUnmapped = true;
      continue;
    }
    if (mapped === 'Other') {
      sawOther = true;
      continue;
    }
    canonical.add(mapped);
  }

  if (canonical.size > 0) {
    return CANONICAL_REGIONS.filter((region) => canonical.has(region));
  }
  if (sawOther || sawUnmapped || full === 'Other') {
    return ['Other'];
  }
  return ['Other'];
}

export function priceSheetName(region: PartitionRegion): string {
  return `Prices — ${region}`;
}

export function coverageSheetName(region: PartitionRegion): string {
  return `Coverage — ${region}`;
}
